/**
 * Public roadmap / feature-request board (FEAT-ROADMAP, Phase 1).
 *
 * Curated by hand - no backend. When a request comes in via the feedback form,
 * add an item here (status "requested") and move it along as work progresses:
 *   requested -> planned -> in-progress -> shipped
 * Phase 2 (after Visualize's Supabase lands) can make this dynamic with upvotes.
 */

export type RoadmapStatus = "in-progress" | "planned" | "requested" | "shipped";

export interface RoadmapItem {
  id: string;
  title: { en: string; id: string };
  blurb: { en: string; id: string };
  status: RoadmapStatus;
  tag?: string;
  /** ISO date, shown for shipped items. */
  date?: string;
}

/** Display order of the sections. */
export const STATUS_ORDER: RoadmapStatus[] = ["in-progress", "planned", "requested", "shipped"];

export const ROADMAP: RoadmapItem[] = [
  // ── In progress ───────────────────────────────────────────────────────────
  {
    id: "viz-3d",
    status: "in-progress",
    tag: "Visualize",
    title: { en: "3D symbology & 3D view", id: "Simbologi 3D & tampilan 3D" },
    blurb: {
      en: "Extrude polygons by an integer field and tilt the map like Google Earth.",
      id: "Ekstrusi poligon berdasar field integer dan miringkan peta seperti Google Earth.",
    },
  },

  // ── Planned ───────────────────────────────────────────────────────────────
  {
    id: "geoparquet-write",
    status: "planned",
    tag: "Converter",
    title: { en: "Write GeoParquet output", id: "Tulis keluaran GeoParquet" },
    blurb: {
      en: "Export to .parquet too (reading is already live) once a WASM Parquet writer is evaluated.",
      id: "Ekspor juga ke .parquet (membaca sudah aktif) setelah penulis Parquet WASM dievaluasi.",
    },
  },
  {
    id: "more-formats",
    status: "planned",
    tag: "Converter",
    title: { en: "TopoJSON & WKT/WKB support", id: "Dukungan TopoJSON & WKT/WKB" },
    blurb: {
      en: "Widen the converter grid further with TopoJSON and WKT/WKB.",
      id: "Perluas lagi matriks konverter dengan TopoJSON dan WKT/WKB.",
    },
  },

  // ── Requested (seed with open ideas) ──────────────────────────────────────
  {
    id: "batch-convert",
    status: "requested",
    tag: "Converter",
    title: { en: "Batch / bulk conversion", id: "Konversi massal / batch" },
    blurb: {
      en: "Convert many files in one go instead of one at a time.",
      id: "Konversi banyak berkas sekaligus, bukan satu per satu.",
    },
  },
  {
    id: "roadmap-votes",
    status: "requested",
    tag: "Site",
    title: { en: "Upvote feature requests", id: "Beri suara untuk permintaan fitur" },
    blurb: {
      en: "Let visitors vote so the most-wanted features rise to the top of this board.",
      id: "Biarkan pengunjung memberi suara agar fitur paling diminati naik ke atas papan ini.",
    },
  },

  // ── Recently shipped ──────────────────────────────────────────────────────
  {
    id: "visualize",
    status: "shipped",
    tag: "Visualize",
    date: "2026-07-11",
    title: { en: "Visualize studio is live", id: "Studio Visualize sudah live" },
    blurb: {
      en: "Upload SHP/GeoJSON, style layers QGIS-style, and share via an end-to-end-encrypted link. Now on shpaid.com/visualize.",
      id: "Unggah SHP/GeoJSON, atur simbologi ala QGIS, dan bagikan lewat tautan terenkripsi. Sudah live di shpaid.com/visualize.",
    },
  },
  {
    id: "kmz-shp",
    status: "shipped",
    tag: "Converter",
    date: "2026-07-11",
    title: { en: "KML/KMZ import (incl. KMZ → SHP)", id: "Impor KML/KMZ (termasuk KMZ → SHP)" },
    blurb: {
      en: "Import Google Earth KML/KMZ and convert to Shapefile, GeoJSON, CSV, GPX or DXF.",
      id: "Impor KML/KMZ Google Earth dan konversi ke Shapefile, GeoJSON, CSV, GPX, atau DXF.",
    },
  },
  {
    id: "geoparquet-read",
    status: "shipped",
    tag: "Converter",
    date: "2026-07-11",
    title: { en: "GeoParquet import", id: "Impor GeoParquet" },
    blurb: {
      en: "Read .parquet files (WKB geometry, SNAPPY/GZIP/ZSTD) and convert to any other format.",
      id: "Baca berkas .parquet (geometri WKB, SNAPPY/GZIP/ZSTD) dan konversi ke format lain.",
    },
  },
  {
    id: "tablet-wheel",
    status: "shipped",
    tag: "UX",
    date: "2026-07-10",
    title: { en: "Tablet-friendly tool wheel", id: "Roda alat ramah tablet" },
    blurb: {
      en: "The donut selector now follows your finger correctly on touch tablets.",
      id: "Pemilih donat kini mengikuti jari dengan benar di tablet sentuh.",
    },
  },
  {
    id: "a11y",
    status: "shipped",
    tag: "Accessibility",
    date: "2026-07-10",
    title: { en: "Keyboard nav & AA contrast", id: "Navigasi keyboard & kontras AA" },
    blurb: {
      en: "Skip-link, full keyboard control of the tool wheel, and WCAG-AA colour contrast.",
      id: "Skip-link, kontrol keyboard penuh pada roda alat, dan kontras warna WCAG-AA.",
    },
  },
  {
    id: "error-monitoring",
    status: "shipped",
    tag: "Reliability",
    date: "2026-07-10",
    title: { en: "Privacy-safe error monitoring", id: "Pemantauan galat yang menjaga privasi" },
    blurb: {
      en: "Crashes are reported anonymously (opt-out honoured) so issues get fixed faster.",
      id: "Galat dilaporkan secara anonim (menghormati opt-out) agar masalah cepat diperbaiki.",
    },
  },
];
